/**
 * Extracts a value from an object using a dot-notation path
 *
 * @param obj - The object to extract value from
 * @param path - The path string (e.g., "a.b.c" or "a[0].b")
 * @param defaultValue - Optional default value if path doesn't exist
 * @returns The value at the path or defaultValue
 */
export function get<T = any>(
  obj: any,
  path: string | string[],
  defaultValue?: T,
): T {
  if (obj == null) {
    return defaultValue as T;
  }

  if (!path || (typeof path === "string" && path.trim() === "")) {
    return defaultValue as T;
  }

  const keys = Array.isArray(path) ? path : path.split(/[.\[\]]/).filter(Boolean);

  if (keys.length === 0) {
    return defaultValue as T;
  }

  if (keys.length === 1) {
    const value = obj[keys[0]];
    return value !== undefined ? value : (defaultValue as T);
  }

  let current = obj;

  for (let i = 0; i < keys.length; i++) {
    if (current == null) {
      return defaultValue as T;
    }

    if (typeof current !== "object" && i < keys.length - 1) {
      return defaultValue as T;
    }

    const key = keys[i];

    if (/^\d+$/.test(key)) {
      current = current[parseInt(key, 10)];
    } else {
      current = current[key];
    }

    if (current === undefined && i < keys.length - 1) {
      return defaultValue as T;
    }
  }

  return current !== undefined ? current : (defaultValue as T);
}
